import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import "./SignupForm.css";
import { environment } from "environment/environment";

const countries = [
  "United States",
  "Canada",
  "United Kingdom",
  "Australia",
  "India",
  "Germany",
  "France",
  "Other",
];

export default function SignupForm() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    userName: "",
    mobileNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
    country: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // 🔹 Input change
  const handleChange = (e) => {
    if (loading) return; // ❌ block editing during loading
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // 🔹 Validation
  const validate = () => {
    const newErrors = {};

    if (!form.userName.trim())
      newErrors.userName = "User Name is required";

    if (!form.mobileNumber.trim())
      newErrors.mobileNumber = "Mobile Number is required";
    else if (!/^\d{10,15}$/.test(form.mobileNumber))
      newErrors.mobileNumber = "Mobile Number must be 10–15 digits";

    if (!form.email.trim())
      newErrors.email = "Email is required";
    else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)
    )
      newErrors.email = "Invalid email address";

    if (!form.password)
      newErrors.password = "Password is required";
    else if (form.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    if (form.confirmPassword !== form.password)
      newErrors.confirmPassword = "Passwords do not match";

    if (!form.country)
      newErrors.country = "Please select a country";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Please fix the errors.");
      return;
    }

    setLoading(true); // 🔒 lock UI

    try {
      const response = await axios.post(
        `${environment.baseUrl}/user/save-user`,
        form
      );

      if (response.status === 200 || response.status === 201) {
        toast.success(response.data.message || "Signup successful!", {
          autoClose: 2000,
          onClose: () => {
            // ✅ navigate ONLY after toast
            navigate("/dashboard");
          },
        });
      } else {
        setLoading(false);
        toast.error("Signup failed. Please try again.");
      }
    } catch (error) {
      setLoading(false);
      toast.error(
        error.response?.data?.message || "Signup failed. Try again."
      );
    }
  };

  return (
    <div className="signup-container">
      <form
        className="signup-form card shadow-sm"
        noValidate
        onSubmit={handleSubmit}
      >
        <h2 className="text-center mb-4">Sign Up</h2>

        {/* Full Name */}
        <div className="mb-3">
          <label className="form-label">Full Name</label>
          <input
            type="text"
            name="userName"
            className={`form-control ${errors.userName ? "is-invalid" : ""}`}
            value={form.userName}
            onChange={handleChange}
            disabled={loading}
          />
          {errors.userName && (
            <div className="invalid-feedback">{errors.userName}</div>
          )}
        </div>

        {/* Mobile */}
        <div className="mb-3">
          <label className="form-label">Mobile Number</label>
          <input
            type="tel"
            name="mobileNumber"
            className={`form-control ${
              errors.mobileNumber ? "is-invalid" : ""
            }`}
            value={form.mobileNumber}
            onChange={handleChange}
            disabled={loading}
          />
          {errors.mobileNumber && (
            <div className="invalid-feedback">{errors.mobileNumber}</div>
          )}
        </div>

        {/* Email */}
        <div className="mb-3">
          <label className="form-label">Email</label>
          <input
            type="email"
            name="email"
            className={`form-control ${errors.email ? "is-invalid" : ""}`}
            value={form.email}
            onChange={handleChange}
            disabled={loading}
          />
          {errors.email && (
            <div className="invalid-feedback">{errors.email}</div>
          )}
        </div>

        {/* Password */}
        <div className="mb-3">
          <label className="form-label">Password</label>
          <input
            type="password"
            name="password"
            className={`form-control ${errors.password ? "is-invalid" : ""}`}
            value={form.password}
            onChange={handleChange}
            disabled={loading}
          />
          {errors.password && (
            <div className="invalid-feedback">{errors.password}</div>
          )}
        </div>

        {/* Confirm Password */}
        <div className="mb-3">
          <label className="form-label">Confirm Password</label>
          <input
            type="password"
            name="confirmPassword"
            className={`form-control ${
              errors.confirmPassword ? "is-invalid" : ""
            }`}
            value={form.confirmPassword}
            onChange={handleChange}
            disabled={loading}
          />
          {errors.confirmPassword && (
            <div className="invalid-feedback">
              {errors.confirmPassword}
            </div>
          )}
        </div>

        {/* Country */}
        <div className="mb-4">
          <label className="form-label">Country</label>
          <select
            name="country"
            className={`form-select ${errors.country ? "is-invalid" : ""}`}
            value={form.country}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">-- Select Country --</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {errors.country && (
            <div className="invalid-feedback">{errors.country}</div>
          )}
        </div>

        {/* 🔥 Spinner Button */}
        <button
          type="submit"
          className="btn btn-primary w-100 d-flex justify-content-center align-items-center"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Signing up...
            </>
          ) : (
            "Sign Up"
          )}
        </button>

        <p className="text-center mt-3">
          Already have an account? <Link to="/login">Login here</Link>
        </p>
      </form>

      <ToastContainer position="top-center" autoClose={2000} />
    </div>
  );
}
